import React, { useState, useEffect } from 'react';
import Pager from '../../components/Pager';
import EditablePageText from '../../components/EditablePageText';
import { isOperator } from '../../utils/permission';
import UploadCounter from './UploadCounter';
import TacticPost from './TacticPost';
import { getMonsterImgPath } from '../../utils/monster';
import Loading from '../../components/Loading';
import { monthDayTime } from '../../utils/date';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import { useBackHandler } from '../../hooks/useBackHandler';

interface StrategyProps {
  externalSelectedTactic?: any;
  onExternalTacticClose?: () => void;
}

const Strategy: React.FC<StrategyProps> = ({ externalSelectedTactic, onExternalTacticClose }) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  /**
   * 다른 화면의 [공략 작성하러 가기] — 이 탭의 작성 창을 그 방덱을 채워 연다(탭 전환은 PageContainer 가 한다).
   */
  const [writeDefense, setWriteDefense] = useState<any[] | null>(null);
  useEffect(() => {
    const on = (e: Event) => {
      const team = (e as CustomEvent).detail?.defenseTeam;
      setWriteDefense(Array.isArray(team) && team.length === 3 ? team : null);
      setUploadModalOpen(true);
    };
    window.addEventListener('swpvpdb:write-tactic', on);
    return () => window.removeEventListener('swpvpdb:write-tactic', on);
  }, []);
  const [user, setUser] = useState<{ uuid: string; nickname: string; role: string; profileImg: string; siteAdmin?: boolean } | null>(null);
  const [tactics, setTactics] = useState<any[]>([]);
  const [selectedTactic, setSelectedTactic] = useState<any>(null);
  const [monsterMapping, setMonsterMapping] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 유저 정보 가져오기
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setUser({ uuid: data.uuid, nickname: data.nickname, role: data.role, profileImg: data.profileImg || '', siteAdmin: !!data.siteAdmin });
        }
      })
      .catch(err => {});
  }, []);

  // 몬스터 매핑 가져오기
  useEffect(() => {
    loadMonsterMapping()
      .then(data => setMonsterMapping(data))
      .catch(err => {});
  }, []);

  // 공략 목록 가져오기
  const fetchTactics = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/tactics');
      if (response.ok) {
        const data = await response.json();
        setTactics(data);
      }
    } catch (error) {
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchTactics();
  }, []);

  // 외부에서 전달된 공략이 있으면 자동으로 열기
  useEffect(() => {
    if (externalSelectedTactic) {
      setSelectedTactic(externalSelectedTactic);
    }
  }, [externalSelectedTactic]);

  const handleTacticClick = async (tactic: any) => {
    // 조회수 증가
    try {
      await fetch('/api/tactics/view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: tactic._id })
      });
    } catch (error) {
    }

    setSelectedTactic(tactic);
  };

  const totalPages = Math.ceil(tactics.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const currentTactics = tactics.slice(startIdx, endIdx);

  // 뒤로 가기: 공략 상세·작성 창을 닫는다
  useBackHandler(uploadModalOpen && !!user, () => { setUploadModalOpen(false); setWriteDefense(null); });
  useBackHandler(!!selectedTactic && !!monsterMapping && !!user, () => {
    setSelectedTactic(null);
    if (onExternalTacticClose) {
      onExternalTacticClose();
    }
  });

  return (
    <div style={{
      width: '100%',
      minHeight: 'calc(100vh - 60px)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      paddingBottom: 'clamp(32px, 6vw, 40px)'
    }}>
      <div style={{
        padding: 'clamp(16px, 3vw, 20px) clamp(12px, 2.5vw, 16px)'
      }}>
        {/* 헤더 */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 'clamp(12px, 2.5vw, 16px)',
          padding: 'clamp(20px, 4vw, 24px) clamp(12px, 2.5vw, 16px) clamp(16px, 3vw, 20px)',
          marginBottom: 'clamp(16px, 3vw, 20px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          position: 'relative',
          zIndex: 100
        }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(12px, 2.5vw, 16px)' }}>
            <h1 style={{
              fontSize: 'clamp(24px, 5vw, 28px)',
              fontWeight: 900,
              margin: 0,
              marginBottom: 'clamp(6px, 1.5vw, 8px)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              display: 'inline-block',
              width: '100%',
              letterSpacing: '-0.5px'
            }}>공략</h1>
            <EditablePageText pageKey="strategy" defaultText="길드 점령전 공략 게시글입니다" canEdit={isOperator(user)} style={{
              color: '#222',
              fontSize: 'clamp(13px, 3.6vw, 15px)',
              margin: 0,
              fontWeight: 600
            }} />
          </div>
        </div>

        {/*
          공략 목록.

          **좌우 여백을 0 으로 둔다.** 예전에는 카드 사방에 16~20px 씩 있었는데, 폰에서
          그 32~40px 은 제목이 몇 글자 더 보일 수 있는 폭이다. 위아래 여백만 남긴다.
          여백이 없어지면 줄무늬 배경이 둥근 모서리를 넘어가므로 `overflow: hidden` 으로
          잘라 낸다.
        */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 'clamp(12px, 2.5vw, 16px)',
          // 위는 머리줄이 바로 붙게 좁힌다(사용자 2026-10-30 "헤더 위에 영역이 너무 많이 남아")
          padding: 'clamp(4px, 1vw, 6px) 0 clamp(16px, 3vw, 20px)',
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          {isLoading ? (
            <Loading padding="clamp(32px, 6vw, 40px)" />
          ) : tactics.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'clamp(32px, 6vw, 40px)', color: '#999' }}>
              등록된 공략이 없습니다
            </div>
          ) : (
            <div>
              {/* 머리줄 — PC 와 같이(사용자 2026-10-30). 칸 폭은 아래 줄과 같다 */}
              <div style={{
                display: 'flex', alignItems: 'center', height: 'clamp(24px, 6vw, 28px)', fontSize: 'clamp(9px, 2vw, 11px)',
                fontWeight: 700, color: '#8a8fb5', borderBottom: '1.5px solid rgba(102, 126, 234, 0.45)'
              }}>
                <span style={{ width: 'clamp(20px, 5vw, 24px)', flexShrink: 0, textAlign: 'center' }}>번호</span>
                <span style={{ flex: 1, minWidth: 0, paddingLeft: 'clamp(6px, 1.5vw, 8px)', textAlign: 'left' }}>제목</span>
                <span style={{ width: 'clamp(120px, 26vw, 128px)', flexShrink: 0, textAlign: 'left' }}>공방덱</span>
                <span style={{ width: 'clamp(66px, 17vw, 72px)', flexShrink: 0, paddingLeft: 'clamp(3px, 0.8vw, 4px)', textAlign: 'left' }}>작성자</span>
              </div>
              {currentTactics.map((tactic, idx) => (
                <div
                  key={tactic._id}
                  style={{
                    borderBottom: idx === currentTactics.length - 1 ? '2px solid rgba(102, 126, 234, 0.25)' : '1px solid rgba(102, 126, 234, 0.3)',
                    background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(248, 250, 255, 0.8)',
                    display: 'flex',
                    // 칸을 세로로 꽉 채운다. `center` 로 두면 칸 높이가 글자 높이만큼만
                    // 되어서 세로 구분선이 행 가운데에 토막으로 뜬다
                    alignItems: 'stretch',
                    minHeight: 'clamp(46px, 9vh, 50px)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative'
                  }}
                  onClick={() => handleTacticClick(tactic)}
                >
                  {/* 1. 순번 */}
                  <div style={{
                    width: 'clamp(20px, 5vw, 24px)',
                    minWidth: 'clamp(20px, 5vw, 24px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 'clamp(9px, 2vw, 11px)',
                    fontWeight: 700,
                    color: '#667eea'
                  }}>
                    {startIdx + idx + 1}
                  </div>

                  {/*
                    2. 제목 — **두 줄까지** 보여준다.

                    한 줄로 자르면 폰에서는 대여섯 글자만 남아 무슨 공략인지 알 수 없었다.
                    `-webkit-line-clamp` 로 두 줄째 끝에 … 이 붙는다(줄 수를 제한하는 표준
                    속성 `line-clamp` 는 아직 브라우저가 안 받는다).
                  */}
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: 'clamp(6px, 1.5vw, 8px)',
                    paddingRight: 'clamp(6px, 1.5vw, 8px)',
                    paddingTop: 'clamp(5px, 1.2vw, 6px)',
                    paddingBottom: 'clamp(5px, 1.2vw, 6px)',
                    fontSize: 'clamp(10px, 2vw, 12px)',
                    fontWeight: 400,
                    color: '#333',
                    lineHeight: 1.35,
                    wordBreak: 'break-word',
                    // `.App` 에 `text-align: center` 가 걸려 있어(CRA 기본값) 그냥 두면
                    // 제목이 가운데로 온다. 두 줄이 되면 특히 어긋나 보인다
                    textAlign: 'left',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    alignSelf: 'center'
                  }}>
                    {tactic.title}
                  </div>

                  {/* 3. 공덱과 방덱 */}
                  {/* 3. 공덱 VS 방덱.
                      오른쪽에 글쓴이 칸(74px)이 붙으므로 몹 그림을 18 → 15px 로 줄여 폭을 낸다.
                      13px 까지 줄여 봤지만 몹을 알아보기 어려웠다 */}
                  <div style={{
                    width: 'clamp(120px, 26vw, 128px)',
                    minWidth: 'clamp(120px, 26vw, 128px)',
                    display: 'flex',
                    alignItems: 'center',
                    // 왼쪽에 붙인다 — 방덱이 없는 글도 공덱 상자 x 가 다른 글과 같게(사용자 2026-10-30, 세로줄도 뺐다)
                    justifyContent: 'flex-start',
                    gap: 'clamp(2px, 0.5vw, 3px)'
                  }}>
                    {/* 공덱 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(1px, 0.3vw, 2px)',
                        background: '#fff',
                        padding: 'clamp(1px, 0.3vw, 2px) clamp(2px, 0.5vw, 3px)',
                        borderRadius: 'clamp(3px, 0.8vw, 4px)',
                        border: '1.5px solid #1976d2',
                        boxShadow: '0 1px 4px rgba(33, 150, 243, 0.3)'
                      }}>
                        {monsterMapping && Array.from({ length: 3 }, (_, idx) => {
                          const m = (tactic.attackTeam || [])[idx] || { name: '', attribute: '' };
                          const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                          return (
                            <img loading="lazy"
                              key={idx}
                              src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                              alt={displayName}
                              style={{
                                width: 'clamp(14px, 3vw, 15px)',
                                height: 'clamp(14px, 3vw, 15px)',
                                borderRadius: 'clamp(2px, 0.5vw, 3px)',
                                objectFit: 'contain',
                                display: 'inline-block',
                                flexShrink: 0
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* VS + 방덱 (방덱이 있을 때만) */}
                    {tactic.defenseTeam && tactic.defenseTeam.length > 0 && tactic.defenseTeam.some((m: any) => m.name) ? (
                      <>
                        <span style={{
                          color: '#667eea',
                          fontWeight: 600,
                          fontSize: 'clamp(8px, 1.8vw, 10px)',
                          letterSpacing: '0.5px',
                          flexShrink: 0
                        }}>
                          VS
                        </span>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(1px, 0.3vw, 2px)',
                            background: '#fff',
                            padding: 'clamp(1px, 0.3vw, 2px) clamp(2px, 0.5vw, 3px)',
                            borderRadius: 'clamp(3px, 0.8vw, 4px)',
                            border: '1.5px solid #d32f2f',
                            boxShadow: '0 1px 4px rgba(244, 67, 54, 0.3)'
                          }}>
                            {monsterMapping && Array.from({ length: 3 }, (_, idx) => {
                              const m = (tactic.defenseTeam || [])[idx] || { name: '', attribute: '' };
                              const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                              return (
                                <img loading="lazy"
                                  key={idx}
                                  src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                  alt={displayName}
                                  style={{
                                    width: 'clamp(14px, 3vw, 15px)',
                                    height: 'clamp(14px, 3vw, 15px)',
                                    borderRadius: 'clamp(2px, 0.5vw, 3px)',
                                    objectFit: 'contain',
                                    display: 'inline-block',
                                    flexShrink: 0
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>

                  {/*
                    4. 글쓴이 · 작성시간 — 오른쪽 끝.

                    프로필 그림을 왼쪽에 두고 닉네임·시간을 오른쪽에 쌓는다. 세로로만 쌓아
                    봤지만(프로필 위, 글자 아래) 칸이 좁아 답답했다. `ProfileWithName` 대신
                    손으로 쓴 이유는 거기서 시간 글자가 `max(10, fontSize-4)` 로 고정돼
                    닉네임보다 커지기 때문이다.
                  */}
                  <div style={{
                    width: 'clamp(66px, 17vw, 72px)',
                    minWidth: 'clamp(66px, 17vw, 72px)',
                    display: 'flex',
                    flexDirection: 'column',
                    // 왼쪽에 붙인다 — 글마다 프로필 x 가 같게(사용자 2026-10-30 "x위치는 서로 틀어지면 안돼").
                    // 예전엔 짧은 닉네임 오른쪽이 비어 보여 가운데로 모았었다
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    gap: 1,
                    paddingLeft: 'clamp(3px, 0.8vw, 4px)',
                    paddingRight: 'clamp(3px, 0.8vw, 4px)'
                  }}>
                    {/* 프로필 그림은 **닉네임과 같은 줄**에 둔다. 시간은 그 아래 줄로 내린다 —
                        그림을 두 줄 옆에 세우면 닉네임과 시간 사이에 걸쳐 보인다 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(3px, 0.8vw, 4px)',
                      maxWidth: '100%',
                      minWidth: 0
                    }}>
                      <img loading="lazy"
                        src={tactic.profileImg || '/images/default-profile.png'}
                        alt="프로필"
                        style={{
                          width: 'clamp(14px, 3.2vw, 16px)',
                          height: 'clamp(14px, 3.2vw, 16px)',
                          borderRadius: 'clamp(3px, 0.8vw, 4px)',
                          objectFit: 'cover',
                          background: '#e3f0ff',
                          border: '1.5px solid #e3f0ff',
                          flexShrink: 0
                        }}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                      />
                      <span style={{
                        fontSize: 'clamp(9px, 2vw, 10px)',
                        fontWeight: 700,
                        color: tactic.isDeleted ? '#ff6b6b' : '#667eea',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {tactic.nickname}
                      </span>
                    </div>
                    {/* 남는 폭을 비워 두지 않고 글자에 쓴다 — 8px 이던 것을 9px 로.
                        `08/10 21:30` 이 43 → 48px 이 되어 칸(내용 폭 60px)을 채운다 */}
                    <span style={{
                      fontSize: 'clamp(9px, 2vw, 10px)',
                      color: '#999',
                      fontWeight: 400,
                      whiteSpace: 'nowrap'
                    }}>
                      {monthDayTime(tactic.createdAt, false)}
                    </span>
                  </div>
                </div>
              ))}

              {/* 페이지네이션 — 둥근 상자 하나(components/Pager), 폰은 번호 5개까지 */}
              <Pager page={currentPage} total={totalPages} onChange={setCurrentPage} compact window={5} />
            </div>
          )}
        </div>
      </div>

      {/* 공략 업로드 모달 */}
      {uploadModalOpen && user && (
        <UploadCounter
          onUpload={async () => {
            await fetchTactics();
            setCurrentPage(1);
            setUploadModalOpen(false);
            setWriteDefense(null);
          }}
          author={user.nickname}
          initialDefense={writeDefense || undefined}
          onClose={() => { setUploadModalOpen(false); setWriteDefense(null); }}
        />
      )}

      {/* 공략 상세 모달 */}
      {selectedTactic && monsterMapping && user && (
        <TacticPost
          open={!!selectedTactic}
          onClose={() => {
            setSelectedTactic(null);
            if (onExternalTacticClose) {
              onExternalTacticClose();
            }
          }}
          tactic={selectedTactic}
          monsterMapping={monsterMapping}
          user={user}
          onTacticDeleted={() => {
            fetchTactics();
            setSelectedTactic(null);
            if (onExternalTacticClose) {
              onExternalTacticClose();
            }
          }}
        />
      )}

      {/* 공략 추가 플로팅 버튼 */}
      <button
        onClick={() => setUploadModalOpen(true)}
        style={{
          position: 'fixed',
          right: 'clamp(16px, 3vw, 20px)',
          bottom: 'clamp(16px, 3vw, 20px)',
          width: 'clamp(48px, 9vw, 52px)',
          height: 'clamp(48px, 9vw, 52px)',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8b9eff 0%, #a78bfa 100%)',
          color: '#fff',
          border: 'none',
          fontSize: 'clamp(20px, 4vw, 24px)',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(139, 158, 255, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        title="공략 추가"
      >
        +
      </button>
    </div>
  );
};

export default Strategy;
